import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Dialog } from "primereact/dialog";
import { useFlowStore } from "../../store/FlowStateStore.ts";
import { CanvasStartFlow } from "../../models/CanvasStartFlow";
import { CanvasNode, CanvasNodeContent, CanvasNodeData } from "../../models/CanvasNode.ts";
import VariablePicker from "../Common/VariablePicker.tsx";
import LinkHandler from "./LinkHandler.tsx";
import TemplateBubble from "../Common/TemplateBubble.tsx";
import { Environment as environment } from "../../environment.ts";
import { noReplyHandleFor } from "../../utils/BodyLinkRules.ts";
import { pruneNoReplyEdges } from "../../utils/EdgeCleanup.ts";

const extractTemplateButtons = (template: any): any[] => {
    const buttonsComp = (template?.components || []).find((c: any) => c.type === "BUTTONS");
    return (buttonsComp?.buttons || []).map((b: any) => ({
        type: b.type,
        text: b.text,
        url: b.url ?? null,
        phone_number: b.phone_number ?? null,
    }));
};

const StartFlow: React.FC<any> = ({ nodeContent, nodeIdx, subNodeIdx }) => {

    const { nodes, setNodes, edges, setEdges, businessId, flowId } = useFlowStore();
    const keywordInputRef = useRef<HTMLInputElement>(null);
    const regexInputRef = useRef<HTMLInputElement>(null);

    const [keywords, setKeywords] = useState<string[]>([]);
    const [regex, setRegex] = useState<string>("");
    const [triggerType, setTriggerType] = useState<string>("KEYWORD");
    const [adSourceIds, setAdSourceIds] = useState<string[]>([]);
    const adSourceInputRef = useRef<HTMLInputElement>(null);
    const [templates, setTemplates] = useState<any[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<any | null>(null);
    const [dialogVisible, setDialogVisible] = useState<boolean>(false);
    const [allTemplates, setAllTemplates] = useState<any[]>([]);

    const [sheetTriggerEnabled, setSheetTriggerEnabled] = useState(false);
    const [sheetSpreadsheetId, setSheetSpreadsheetId] = useState("");
    const [sheetSpreadsheetName, setSheetSpreadsheetName] = useState("");
    const [sheetTab, setSheetTab] = useState("");
    const [sheetPhoneColumn, setSheetPhoneColumn] = useState("");
    const [sheetMappings, setSheetMappings] = useState<{column: string; attributeId: string}[]>([]);
    const [sheetPickerLoading, setSheetPickerLoading] = useState(false);
    const [sheetTabs, setSheetTabs] = useState<string[]>([]);
    const [sheetHeaders, setSheetHeaders] = useState<string[]>([]);
    const [sheetAttributes, setSheetAttributes] = useState<{attributeId: string; attributeName: string}[]>([]);
    const [sheetError, setSheetError] = useState("");
    const [sheetDialogVisible, setSheetDialogVisible] = useState(false);

    const [qrTriggerEnabled, setQrTriggerEnabled] = useState(false);
    // Which keywords get a code. Empty means every keyword, so a keyword added later is included.
    // The codes themselves are created by the backend on save and read straight from the node data.
    const [qrKeywords, setQrKeywords] = useState<string[]>([]);

    const [fbLeadTriggerEnabled, setFbLeadTriggerEnabled] = useState(false);
    const [fbLeadPageId, setFbLeadPageId] = useState("");
    const [fbLeadPageName, setFbLeadPageName] = useState("");
    const [fbLeadFormId, setFbLeadFormId] = useState("");
    const [fbLeadFormName, setFbLeadFormName] = useState("");
    const [fbLeadFieldMappings, setFbLeadFieldMappings] = useState<{fieldName: string; attributeId: string}[]>([]);
    const [fbLeadDialogVisible, setFbLeadDialogVisible] = useState(false);
    const [fbConnected, setFbConnected] = useState(false);
    const [fbPages, setFbPages] = useState<{id: string; name: string}[]>([]);
    const [fbForms, setFbForms] = useState<{id: string; name: string}[]>([]);

    useEffect(() => {
        const node = nodes.filter((node) => (node.data as CanvasNodeData).type == "FLOW_START")[0];
        if (!!node) {
            const content = (node.data as CanvasNodeData).content.filter((con) => con.type == "FLOW_START")[0];
            if (!!content) {
                const d = content.data as any;
                setKeywords(d.keywords || []);
                setRegex(d.regex || "");
                setTriggerType(d.triggerType || "KEYWORD");
                // A flow used to answer exactly one ad, held in the scalar `adSourceId`. It now
                // holds a list. Both are read so a flow saved by the old builder still shows its
                // ad — the document upgrades the next time this node is saved.
                setAdSourceIds(
                    d.adSourceIds?.length
                        ? d.adSourceIds
                        : (d.adSourceId ? [d.adSourceId] : [])
                );
                const savedTemplates = (d.templates || []).map((t: any) => ({
                    ...t,
                    buttons: t.buttons && t.buttons.length > 0 ? t.buttons : extractTemplateButtons(t)
                }));
                setTemplates(savedTemplates);
                if (savedTemplates.length > 0) setSelectedTemplate(savedTemplates[0]);
                if (d.sheetTriggerEnabled) {
                    setSheetTriggerEnabled(true);
                    setSheetSpreadsheetId(d.sheetSpreadsheetId || "");
                    setSheetSpreadsheetName(d.sheetSpreadsheetName || "");
                    setSheetTab(d.sheetTab || "");
                    setSheetPhoneColumn(d.sheetPhoneColumn || "");
                    setSheetMappings(d.sheetMappings || []);
                    if (d.sheetSpreadsheetId && businessId) {
                        axios.get(`${environment.baseUrl}/google/sheets/${d.sheetSpreadsheetId}/tabs?businessId=${businessId}`)
                            .then(r => setSheetTabs(r.data || [])).catch(() => {});
                        if (d.sheetTab) {
                            axios.get(`${environment.baseUrl}/google/sheets/${d.sheetSpreadsheetId}/columns?businessId=${businessId}&sheetName=${encodeURIComponent(d.sheetTab)}`)
                                .then(r => setSheetHeaders(r.data || [])).catch(() => {});
                        }
                    }
                }
                setQrTriggerEnabled(d.qrTriggerEnabled || false);
                setQrKeywords(d.qrKeywords || []);
                setFbLeadTriggerEnabled(d.fbLeadTriggerEnabled || false);
                setFbLeadPageId(d.fbLeadPageId || "");
                setFbLeadPageName(d.fbLeadPageName || "");
                setFbLeadFormId(d.fbLeadFormId || "");
                setFbLeadFormName(d.fbLeadFormName || "");
                setFbLeadFieldMappings(d.fbLeadFieldMappings || []);
            }
        }
    }, []);

    useEffect(() => {
        if (!businessId || !dialogVisible) return;
        const payload = { businessId, page: 0, size: 100 };
        axios.put(`${environment.baseUrl}/message/template`, payload)
            .then((res) => {
                const approved = (res.data?.data?.data ?? []).filter((t: any) => t.templateStatus === "APPROVED");
                setAllTemplates(approved);
            })
            .catch(() => setAllTemplates([]));
    }, [businessId, dialogVisible]);

    const flowStartNodeId = nodes.filter((n) => (n.data as CanvasNodeData).type === "FLOW_START")[0]?.id;

    const pickTemplate = (template: any) => {
        const withButtons = { ...template, buttons: extractTemplateButtons(template) };
        setSelectedTemplate(withButtons);
        setTemplates([withButtons]);
        pruneNoReplyEdges(flowStartNodeId, noReplyHandleFor(withButtons.templateId), edges, setEdges);
        setDialogVisible(false);
    };

    const clearTemplate = () => {
        setSelectedTemplate(null);
        setTemplates([]);
        pruneNoReplyEdges(flowStartNodeId, null, edges, setEdges);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && e.currentTarget.value?.length > 0) {
            setKeywords([...keywords, e.currentTarget.value]);
            e.currentTarget.value = "";
        }
    };

    const handleVariableSelect = (varName: string, target: 'keyword' | 'regex') => {
        const input = target === 'keyword' ? keywordInputRef.current : regexInputRef.current;
        if (input) {
            const start = input.selectionStart || 0;
            const end = input.selectionEnd || 0;
            const val = input.value;
            const insertion = `{{${varName}}}`;
            const newVal = val.substring(0, start) + insertion + val.substring(end);
            
            if (target === 'keyword') {
                // For keywords, we might want to just add it as a full keyword or let them edit it
                input.value = newVal;
            } else {
                setRegex(newVal);
            }
            input.focus();
        }
    };

    const removeKeyword = (idx: number) => {
        setKeywords([...keywords.filter((_, keyIdx) => (keyIdx != idx))]);
    }

    /** Ad ids one flow may answer. Mirrors the backend's CtwaTriggerAccessor.MAX_AD_IDS. */
    const MAX_AD_SOURCE_IDS = 20;

    const addAdSourceId = (raw: string) => {
        const id = raw.trim();
        if (!id) return;
        // Silently ignoring a duplicate rather than warning: re-typing an id that is already in
        // the list is a correction, not an error worth interrupting for.
        if (adSourceIds.some((existing) => existing.toUpperCase() === id.toUpperCase())) return;
        if (adSourceIds.length >= MAX_AD_SOURCE_IDS) return;
        setAdSourceIds([...adSourceIds, id]);
    };

    const handleAdSourceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && e.currentTarget.value?.length > 0) {
            addAdSourceId(e.currentTarget.value);
            e.currentTarget.value = "";
        }
    };

    const removeAdSourceId = (idx: number) => {
        setAdSourceIds([...adSourceIds.filter((_, i) => i !== idx)]);
    };

    useEffect(() => {
        if (nodes.length > 0) {
            const firstNode = nodes[0];
            const firstContent = (firstNode.data as CanvasNodeData).content[0];
            
            const node: CanvasNode = {
                ...firstNode,
                data: {
                    ...firstNode.data,
                    content: [
                        {
                            ...firstContent,
                            id: firstContent.id,
                            type: "FLOW_START",
                            data: {
                                ...(firstContent.data || {}),
                                keywords,
                                regex,
                                templates,
                                triggerType,
                                adSourceIds,
                                // Cleared, not carried: the backend reads the union of both fields,
                                // so leaving a stale scalar behind would keep routing an ad the
                                // user just removed from the list.
                                adSourceId: undefined,
                                sheetTriggerEnabled,
                                sheetSpreadsheetId,
                                sheetSpreadsheetName,
                                sheetTab,
                                sheetPhoneColumn,
                                sheetMappings,
                                fbLeadTriggerEnabled,
                                fbLeadPageId,
                                fbLeadPageName,
                                fbLeadFormId,
                                fbLeadFormName,
                                fbLeadFieldMappings,
                                qrTriggerEnabled,
                                qrKeywords
                            }
                        } as any
                    ]
                }
            }
            setNodes([node, ...nodes.slice(1)]);
        }
    }, [keywords, regex, templates, triggerType, adSourceIds,
        sheetTriggerEnabled, sheetSpreadsheetId, sheetSpreadsheetName,
        sheetTab, sheetPhoneColumn, sheetMappings,
        fbLeadTriggerEnabled, fbLeadPageId, fbLeadPageName,
        fbLeadFormId, fbLeadFormName, fbLeadFieldMappings,
        qrTriggerEnabled, qrKeywords]);

    useEffect(() => {
        if (keywords.length === 0 && qrTriggerEnabled) {
            setQrTriggerEnabled(false);
        }
    }, [keywords, qrTriggerEnabled]);

    // A selection naming keywords that no longer exist would keep asking for codes nobody can scan.
    useEffect(() => {
        const pruned = qrKeywords.filter((keyword) => keywords.includes(keyword));
        if (pruned.length !== qrKeywords.length) setQrKeywords(pruned);
    }, [keywords, qrKeywords]);

    const handleQrToggle = (enabled: boolean) => {
        if (enabled && keywords.length === 0) return;
        setQrTriggerEnabled(enabled);
        if (enabled) setQrKeywords([]);
    };

    /** The keywords a code is wanted for — an empty selection means all of them. */
    const qrSelection = qrKeywords.length > 0 ? qrKeywords : keywords;

    const toggleQrKeyword = (keyword: string) => {
        const next = qrSelection.includes(keyword)
            ? qrSelection.filter((candidate) => candidate !== keyword)
            : [...qrSelection, keyword];

        // Unticking the last one is the same intent as turning the trigger off.
        if (next.length === 0) {
            setQrTriggerEnabled(false);
            setQrKeywords([]);
            return;
        }
        const ordered = keywords.filter((candidate) => next.includes(candidate));
        setQrKeywords(ordered.length === keywords.length ? [] : ordered);
    };

    useEffect(() => {
        if (!sheetSpreadsheetId || !businessId) return;
        axios.get(`${environment.baseUrl}/google/sheets/${sheetSpreadsheetId}/tabs?businessId=${businessId}`)
            .then(res => setSheetTabs(res.data || [])).catch(() => {});
    }, [sheetSpreadsheetId]);

    useEffect(() => {
        if (!sheetDialogVisible || !businessId) return;
        if (sheetAttributes.length === 0) {
            axios.put(`${environment.baseUrl}/fetch/attribute`, { businessId, page: 0, size: 100 })
                .then(res => setSheetAttributes(res.data?.data?.data || [])).catch(() => {});
        }
    }, [sheetDialogVisible]);

    useEffect(() => {
        if (!fbLeadDialogVisible || !businessId) return;
        axios.get(`${environment.baseUrl}/facebook/status?businessId=${businessId}`)
            .then(r => {
                setFbConnected(r.data.connected);
                if (r.data.connected) {
                    axios.get(`${environment.baseUrl}/facebook/pages?businessId=${businessId}`)
                        .then(r => setFbPages(r.data || [])).catch(() => {});
                    if (sheetAttributes.length === 0) {
                        axios.put(`${environment.baseUrl}/fetch/attribute`, { businessId, page: 0, size: 100 })
                            .then(res => setSheetAttributes(res.data?.data?.data || [])).catch(() => {});
                    }
                }
            }).catch(() => setFbConnected(false));
    }, [fbLeadDialogVisible]);

    useEffect(() => {
        if (!fbLeadPageId || !businessId) return;
        axios.get(`${environment.baseUrl}/facebook/pages/${fbLeadPageId}/forms?businessId=${businessId}`)
            .then(r => setFbForms(r.data || [])).catch(() => {});
    }, [fbLeadPageId]);

    const handleFbLeadToggle = (enabled: boolean) => {
        if (!enabled) { setFbLeadTriggerEnabled(false); resetFbLeadState(); }
        else { setFbLeadDialogVisible(true); }
    };

    const resetFbLeadState = () => {
        setFbLeadPageId(""); setFbLeadPageName("");
        setFbLeadFormId(""); setFbLeadFormName(""); setFbLeadFieldMappings([]);
    };

    const saveFbLeadTrigger = () => {
        setFbLeadTriggerEnabled(true);
        setFbLeadDialogVisible(false);
    };

    const getPickerOrigin = (): string => {
        if (window.self !== window.top && document.referrer) {
            return new URL(document.referrer).origin;
        }
        return window.location.origin;
    };

    const openSheetPicker = () => {
        setSheetPickerLoading(true);
        axios.get(`${environment.baseUrl}/google/access-token?businessId=${businessId}`)
            .then((res) => {
                const accessToken = res.data.accessToken;
                const launchPicker = () => {
                    (window as any).gapi.load('picker', () => {
                        const STYLE_ID = 'gpicker-zindex-fix';
                        if (!document.getElementById(STYLE_ID)) {
                            const s = document.createElement('style');
                            s.id = STYLE_ID;
                            s.textContent = '.picker-dialog-bg{z-index:2147483646!important}.picker-dialog{z-index:2147483647!important}';
                            document.head.appendChild(s);
                        }
                        const picker = new (window as any).google.picker.PickerBuilder()
                            .addView(
                                new (window as any).google.picker.DocsView()
                                    .setMimeTypes('application/vnd.google-apps.spreadsheet')
                            )
                            .setOAuthToken(accessToken)
                            .setOrigin(getPickerOrigin())
                            .setCallback((data: any) => {
                                if (data.action === 'picked' || data.action === 'cancel') {
                                    document.getElementById(STYLE_ID)?.remove();
                                }
                                if (data.action === 'picked') {
                                    const file = data.docs[0];
                                    setSheetSpreadsheetId(file.id);
                                    setSheetSpreadsheetName(file.name);
                                    setSheetTab('');
                                    setSheetTabs([]);
                                }
                            })
                            .build();
                        picker.setVisible(true);
                        setSheetPickerLoading(false);
                    });
                };
                if ((window as any).google?.picker) {
                    launchPicker();
                } else {
                    const script = document.createElement('script');
                    script.src = 'https://apis.google.com/js/api.js';
                    script.onload = launchPicker;
                    document.body.appendChild(script);
                }
            })
            .catch(() => setSheetPickerLoading(false));
    };

    const handleSheetToggle = (enabled: boolean) => {
        setSheetError("");
        if (!enabled) {
            setSheetTriggerEnabled(false);
            setSheetSpreadsheetId(""); setSheetSpreadsheetName("");
            setSheetTab(""); setSheetPhoneColumn(""); setSheetMappings([]); setSheetTabs([]);
        } else {
            setSheetDialogVisible(true);
        }
    };

    const saveSheetTrigger = () => {
        setSheetTriggerEnabled(true);
        setSheetDialogVisible(false);
        setSheetError("");
    };

    // Codes are derived state owned by the backend: read, never written, from the node data the
    // last save returned. A flow saved before `qrCodes` existed still shows its single code.
    const savedQrCodes: { keyword: string; code: string; shortUrl: string }[] = (() => {
        const node = nodes.find((n) => (n.data as CanvasNodeData).type === "FLOW_START");
        if (!node) return [];
        const content = (node.data as CanvasNodeData).content.find((c) => c.type === "FLOW_START");
        const data = content?.data as any;
        if (!data) return [];
        if (Array.isArray(data.qrCodes) && data.qrCodes.length > 0) return data.qrCodes;
        return data.qrShortUrl
            ? [{ keyword: data.qrKeyword || "", code: data.qrCode || "", shortUrl: data.qrShortUrl }]
            : [];
    })();

    const pendingQrKeywords = qrTriggerEnabled
        ? qrSelection.filter((keyword) => !savedQrCodes.some((qr) => qr.keyword === keyword))
        : [];

    return (
        <div className="node-body" style={{ border: 'none' }}>
            <div className="mb-4">
                <div className="font-label mb-2">Trigger Type</div>
                <div className="flex flex-col gap-2">
                    <label className="flex items-start gap-1 cursor-pointer text-[11px] text-slate-700 hover:text-slate-900 transition-colors">
                        <input
                            type="radio"
                            name="triggerType"
                            value="KEYWORD"
                            checked={triggerType === "KEYWORD"}
                            onChange={(e) => {
                                setTriggerType(e.target.value);
                                if (e.target.value !== "KEYWORD") setQrTriggerEnabled(false);
                            }}
                            className="accent-[var(--primary-bg-color)] flex-shrink-0 m-0"
                            style={{ width: '12px', height: '12px', marginTop: '2px' }}
                        />
                        <span className="leading-tight">Keyword/Regex</span>
                    </label>
                    <label className="flex items-start gap-1 cursor-pointer text-[11px] text-slate-700 hover:text-slate-900 transition-colors">
                        <input
                            type="radio"
                            name="triggerType"
                            value="CTWA"
                            checked={triggerType === "CTWA"}
                            onChange={(e) => setTriggerType(e.target.value)}
                            className="accent-[var(--primary-bg-color)] flex-shrink-0 m-0"
                            style={{ width: '12px', height: '12px', marginTop: '2px' }}
                        />
                        <span className="leading-tight">Click-to-WhatsApp Ad</span>
                    </label>
                </div>
            </div>

            {triggerType === "KEYWORD" ? (
                <>
                    <div>
                        <div className="font-label flex justify-between items-center">
                            <span>Type press enter to add keyword <span className="text-red-500">*</span></span>
                            <VariablePicker onSelect={(v) => handleVariableSelect(v, 'keyword')} label="" />
                        </div>
                        <input
                            ref={keywordInputRef}
                            placeholder="Enter keywords"
                            onKeyDown={handleKeyDown}
                            style={{ backgroundColor: 'white', fontSize: '11px', width: '100%', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd' }}
                        />
                        {keywords.length === 0 && (
                            <div style={{ fontSize: "10px", color: "#64748b", marginTop: "4px" }}>
                                Add a keyword, or a regex below, to activate this trigger.
                            </div>
                        )}
                    </div>
                    <div className="mt-2">
                        {keywords.length > 0 &&
                            <div className="keyword-container flex flex-wrap gap-1">
                                {keywords.map((keyword, idx) => (
                                    <div key={idx} className="keywords flex items-center gap-1 bg-slate-100 px-2 py-0.5 rounded text-[10px]">
                                        <span>{keyword}</span>
                                        <img 
                                            src={require("../../assets/common/delete.png")} 
                                            className="cursor-pointer hover:opacity-70" 
                                            style={{ height: '8px' }} 
                                            onClick={() => removeKeyword(idx)} 
                                        />
                                    </div>
                                ))}
                            </div>
                        }
                    </div>
                    <div className="mt-4">
                        <div className="font-label flex justify-between items-center">
                            <span>Enter Regex trigger</span>
                            <VariablePicker onSelect={(v) => handleVariableSelect(v, 'regex')} label="" />
                        </div>
                        <input 
                            ref={regexInputRef}
                            placeholder="Enter regex pattern" 
                            value={regex} 
                            onChange={(event) => { setRegex(event.target.value); }} 
                            style={{ backgroundColor: 'white', fontSize: '11px', width: '100%', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd' }} 
                        />
                    </div>

                    <div style={{ marginTop: "12px", padding: "8px 10px", background: "#f0fdf4", borderRadius: "6px", border: "1px solid #bbf7d0" }}>
                        <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: keywords.length === 0 ? "not-allowed" : "pointer", margin: 0, opacity: keywords.length === 0 ? 0.6 : 1 }}>
                            <input
                                type="checkbox"
                                checked={qrTriggerEnabled}
                                disabled={keywords.length === 0}
                                onChange={(e) => handleQrToggle(e.target.checked)}
                                style={{ width: "fit-content" }}
                            />
                            <span style={{ fontSize: "11px", fontWeight: 600, color: "#15803d" }}>
                                Create QR Code for keyword
                            </span>
                        </label>

                        {qrTriggerEnabled && keywords.length > 1 && (
                            <div style={{ marginTop: "8px" }}>
                                <div style={{ fontSize: "10px", color: "#64748b", marginBottom: "4px" }}>
                                    One QR code per keyword. Each counts towards your plan.
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {keywords.map((keyword) => (
                                        <label
                                            key={keyword}
                                            className="flex items-center gap-1 cursor-pointer"
                                            style={{ fontSize: "10px", color: "#15803d", margin: 0 }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={qrSelection.includes(keyword)}
                                                onChange={() => toggleQrKeyword(keyword)}
                                                style={{ width: "fit-content" }}
                                            />
                                            <span>{keyword}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {savedQrCodes.length > 0 && (
                            <div style={{ marginTop: "6px" }}>
                                {savedQrCodes.map((qr) => (
                                    <div key={qr.code} style={{ fontSize: "10px", color: "#15803d", marginTop: "2px", wordBreak: "break-all" }}>
                                        {qr.keyword ? <b>{qr.keyword}</b> : "QR link"}:{" "}
                                        <a href={qr.shortUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#15803d" }}>
                                            {qr.shortUrl}
                                        </a>
                                    </div>
                                ))}
                            </div>
                        )}

                        {pendingQrKeywords.length > 0 && (
                            <div style={{ fontSize: "10px", color: "#64748b", marginTop: "6px", lineHeight: 1.4 }}>
                                Save to generate a QR code for {pendingQrKeywords.map((k) => `"${k}"`).join(", ")}.
                                Scanning one opens WhatsApp with that keyword typed in.
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-600">
                    <p className="mb-2">
                        This flow will trigger automatically when a user clicks your <b>Facebook or Instagram Ad</b>.
                    </p>
                    <div className="font-label mb-1 flex justify-between items-center">
                        <span>Ad IDs <span className="text-red-500">*</span></span>
                        <span style={{ fontSize: "10px", color: "#94a3b8", fontWeight: 400 }}>
                            {adSourceIds.length} of {MAX_AD_SOURCE_IDS}
                        </span>
                    </div>
                    <input
                        ref={adSourceInputRef}
                        type="text"
                        placeholder="Paste an ad ID and press enter"
                        onKeyDown={handleAdSourceKeyDown}
                        disabled={adSourceIds.length >= MAX_AD_SOURCE_IDS}
                        style={{
                            backgroundColor: adSourceIds.length >= MAX_AD_SOURCE_IDS ? '#f1f5f9' : 'white',
                            fontSize: '11px', width: '100%', padding: '4px 8px',
                            borderRadius: '4px', border: '1px solid #ddd'
                        }}
                    />
                    {adSourceIds.length === 0 && (
                        <div style={{ fontSize: "10px", color: "#64748b", marginTop: "4px" }}>
                            Add at least one ad ID to activate this trigger.
                        </div>
                    )}
                    {adSourceIds.length >= MAX_AD_SOURCE_IDS && (
                        <div style={{ fontSize: "10px", color: "#b45309", marginTop: "4px" }}>
                            This flow already answers {MAX_AD_SOURCE_IDS} ads, the maximum. Remove one to add another.
                        </div>
                    )}
                    {adSourceIds.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {adSourceIds.map((id, idx) => (
                                <div key={id} className="flex items-center gap-1 bg-white border border-slate-200 px-2 py-0.5 rounded text-[10px]">
                                    <span style={{ fontFamily: 'ui-monospace, monospace' }}>{id}</span>
                                    <img
                                        src={require("../../assets/common/delete.png")}
                                        className="cursor-pointer hover:opacity-70"
                                        style={{ height: '8px' }}
                                        onClick={() => removeAdSourceId(idx)}
                                        alt="Remove"
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="mt-4">
                <div className="font-label">Add upto 1 template to begin flow</div>
                {selectedTemplate ? (
                    <>
                        <div style={{ marginTop: '6px' }}>
                            <TemplateBubble
                                headerText={selectedTemplate.components?.find((c: any) => c.type === "HEADER")?.text}
                                bodyText={selectedTemplate.components?.find((c: any) => c.type === "BODY")?.text}
                                footerText={selectedTemplate.components?.find((c: any) => c.type === "FOOTER")?.text}
                                buttons={selectedTemplate.buttons}
                                renderButtonExtra={(btn) => btn.type === "QUICK_REPLY" ? <LinkHandler idx={btn.text} /> : null}
                            />
                            {selectedTemplate.templateId && (
                                <div
                                    className="relative flex items-center justify-between gap-2 mt-1 px-2 py-1 border border-dashed border-slate-300 rounded"
                                    style={{ fontSize: '10px', color: '#64748b' }}
                                >
                                    <span>If no reply</span>
                                    <span style={{ fontSize: '9px', color: '#94a3b8' }}>connect a Smart Delay</span>
                                    <LinkHandler idx={noReplyHandleFor(selectedTemplate.templateId)} />
                                </div>
                            )}
                            <div
                                className="flex items-center justify-between gap-2 mt-1 px-2 py-1"
                                style={{ fontSize: '10px', color: '#64748b' }}
                            >
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {selectedTemplate.name}
                                </span>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setDialogVisible(true)}
                                        style={{ fontSize: '10px', color: 'var(--primary-bg-color)', background: 'none', border: 'none', cursor: 'pointer' }}
                                    >
                                        Change
                                    </button>
                                    <button
                                        type="button"
                                        onClick={clearTemplate}
                                        style={{ fontSize: '10px', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <button
                        type="button"
                        onClick={() => setDialogVisible(true)}
                        className="choose-template-btn w-full text-[11px] py-1.5 border border-dashed border-slate-300 rounded hover:bg-slate-50 transition-colors"
                    >
                        Choose Template
                    </button>
                )}
            </div>

            <Dialog
                header="Choose Template"
                visible={dialogVisible}
                modal
                appendTo={document.body}
                maskStyle={{ background: "rgba(0,0,0,0.6)" }}
                style={{ width: "80vw" }}
                onHide={() => setDialogVisible(false)}
            >
                {allTemplates.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '12px' }}>
                        No approved templates found for this business.
                    </div>
                ) : (
                    <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '8px', padding: '8px 12px', borderBottom: '1px solid #e2e8f0', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                            <div>Name</div>
                            <div>Category</div>
                            <div>Type</div>
                            <div>Status</div>
                        </div>
                        {allTemplates.map((template) => (
                            <div
                                key={template.templateId}
                                onClick={() => pickTemplate(template)}
                                style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '8px', padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '12px', cursor: 'pointer' }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                            >
                                <div style={{ fontWeight: 600 }}>{template.name}</div>
                                <div>{template.category}</div>
                                <div>{template.templateType}</div>
                                <div style={{ color: '#16a34a' }}>{template.templateStatus}</div>
                            </div>
                        ))}
                    </div>
                )}
            </Dialog>

            <div style={{ marginTop: "12px", padding: "8px 10px", background: "#f0fafa", borderRadius: "6px", border: "1px solid #c8e6e8", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", margin: 0 }}>
                    <input type="checkbox" checked={sheetTriggerEnabled}
                        onChange={(e) => handleSheetToggle(e.target.checked)} />
                    <span style={{ fontSize: "11px", fontWeight: 600, color: "var(--primary-bg-color)", whiteSpace: "nowrap" }}>
                        Google Sheet Trigger
                    </span>
                </label>
                {sheetTriggerEnabled && (
                    <button onClick={() => setSheetDialogVisible(true)}
                        style={{ fontSize: "10px", color: "var(--primary-bg-color)", background: "none", border: "1px solid #c8e6e8", borderRadius: "4px", padding: "2px 8px", cursor: "pointer" }}>
                        Edit
                    </button>
                )}
            </div>

            <Dialog
                header="Google Sheet Trigger"
                visible={sheetDialogVisible}
                modal
                appendTo={document.body}
                maskStyle={{ background: "rgba(0,0,0,0.6)" }}
                style={{ width: "420px" }}
                onHide={() => {
                    setSheetDialogVisible(false);
                    if (!sheetTriggerEnabled) {
                        setSheetSpreadsheetId(""); setSheetSpreadsheetName("");
                        setSheetTab(""); setSheetPhoneColumn(""); setSheetMappings([]);
                    }
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {sheetSpreadsheetName ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ flex: 1, fontSize: "13px", padding: "6px 10px", border: "1px solid #ddd", borderRadius: "4px", background: "#f8fafc" }}>
                                {sheetSpreadsheetName}
                            </span>
                            <button onClick={openSheetPicker} disabled={sheetPickerLoading}
                                style={{ fontSize: "12px", color: "var(--primary-bg-color)", background: "none", border: "1px solid #c8e6e8", borderRadius: "4px", padding: "5px 10px", cursor: "pointer", whiteSpace: "nowrap" }}>
                                {sheetPickerLoading ? "Opening..." : "Change"}
                            </button>
                        </div>
                    ) : (
                        <button onClick={openSheetPicker} disabled={sheetPickerLoading}
                            style={{ width: "100%", fontSize: "13px", padding: "6px 10px", borderRadius: "4px", border: "1px solid #ddd", cursor: "pointer", background: "white", color: "#64748b" }}>
                            {sheetPickerLoading ? "Opening..." : "Choose from Google Drive"}
                        </button>
                    )}

                    <select value={sheetTab} onChange={(e) => {
                            setSheetTab(e.target.value);
                            setSheetPhoneColumn("");
                            setSheetHeaders([]);
                            if (e.target.value && sheetSpreadsheetId) {
                                axios.get(`${environment.baseUrl}/google/sheets/${sheetSpreadsheetId}/columns?businessId=${businessId}&sheetName=${encodeURIComponent(e.target.value)}`)
                                    .then(r => setSheetHeaders(r.data || [])).catch(() => {});
                            }
                        }}
                        disabled={!sheetSpreadsheetId}
                        style={{ width: "100%", fontSize: "13px", padding: "6px 10px", borderRadius: "4px", border: "1px solid #ddd" }}>
                        <option value="">-- Select tab --</option>
                        {sheetTabs.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>

                    <select value={sheetPhoneColumn} onChange={(e) => setSheetPhoneColumn(e.target.value)}
                        disabled={!sheetTab || sheetHeaders.length === 0}
                        style={{ width: "100%", fontSize: "13px", padding: "6px 10px", borderRadius: "4px", border: "1px solid #ddd" }}>
                        <option value="">{sheetHeaders.length === 0 ? (sheetTab ? "Loading..." : "-- Select tab first --") : "-- Phone column --"}</option>
                        {sheetHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>

                    <div>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "6px" }}>
                            Map columns → attributes (for template variables)
                        </div>
                        {sheetMappings.map((m, i) => (
                            <div key={i} style={{ display: "flex", gap: "8px", marginBottom: "6px" }}>
                                <select value={m.column}
                                    onChange={(e) => { const u = [...sheetMappings]; u[i] = { ...u[i], column: e.target.value }; setSheetMappings(u); }}
                                    style={{ width: "130px", fontSize: "13px", padding: "6px 8px", borderRadius: "4px", border: "1px solid #ddd" }}>
                                    <option value="">-- Column --</option>
                                    {sheetHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                                </select>
                                <select value={m.attributeId}
                                    onChange={(e) => { const u = [...sheetMappings]; u[i] = { ...u[i], attributeId: e.target.value }; setSheetMappings(u); }}
                                    style={{ flex: 1, fontSize: "13px", padding: "6px 8px", borderRadius: "4px", border: "1px solid #ddd" }}>
                                    <option value="">-- Attribute --</option>
                                    {sheetAttributes.map(a => <option key={a.attributeId} value={a.attributeId}>{a.attributeName}</option>)}
                                </select>
                                <button onClick={() => setSheetMappings(sheetMappings.filter((_, idx) => idx !== i))}
                                    style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: "18px", lineHeight: 1 }}>×</button>
                            </div>
                        ))}
                        <button onClick={() => setSheetMappings([...sheetMappings, { column: "", attributeId: "" }])}
                            style={{ fontSize: "12px", color: "var(--primary-bg-color)", background: "none", border: "1px solid #c8e6e8", borderRadius: "4px", padding: "3px 10px", cursor: "pointer" }}>
                            + Add mapping
                        </button>
                    </div>

                    {sheetError && <div style={{ color: "#dc2626", fontSize: "12px" }}>{sheetError}</div>}
                    <button onClick={saveSheetTrigger}
                        disabled={!sheetSpreadsheetId || !sheetTab || !sheetPhoneColumn}
                        style={{ width: "100%", background: "var(--primary-bg-color)", color: "white", border: "none", borderRadius: "4px", padding: "8px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                        Save Trigger
                    </button>
                </div>
            </Dialog>

            <div style={{ marginTop: "8px", padding: "8px 10px", background: "#eff6ff", borderRadius: "6px", border: "1px solid #bfdbfe", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", margin: 0 }}>
                    <input type="checkbox" checked={fbLeadTriggerEnabled}
                        onChange={(e) => handleFbLeadToggle(e.target.checked)} />
                    <span style={{ fontSize: "11px", fontWeight: 600, color: "#1d4ed8", whiteSpace: "nowrap" }}>
                        Facebook Lead Ad Trigger
                    </span>
                </label>
                {fbLeadTriggerEnabled && (
                    <button onClick={() => setFbLeadDialogVisible(true)}
                        style={{ fontSize: "10px", color: "#1d4ed8", background: "none", border: "1px solid #bfdbfe", borderRadius: "4px", padding: "2px 8px", cursor: "pointer" }}>
                        Edit
                    </button>
                )}
            </div>

            <Dialog
                header="Facebook Lead Ad Trigger"
                visible={fbLeadDialogVisible}
                modal
                appendTo={document.body}
                maskStyle={{ background: "rgba(0,0,0,0.6)" }}
                style={{ width: "420px" }}
                onHide={() => { setFbLeadDialogVisible(false); if (!fbLeadTriggerEnabled) resetFbLeadState(); }}
            >
                {!fbConnected ? (
                    <div style={{ textAlign: "center", padding: "20px", fontSize: "13px", color: "#475569" }}>
                        <p style={{ marginBottom: "12px" }}>Connect your Facebook account to use this trigger.</p>
                        <button
                            onClick={() => window.open(`${environment.baseUrl}/facebook/authorize?businessId=${businessId}`, '_blank')}
                            style={{ background: "#1877F2", color: "white", border: "none", borderRadius: "6px", padding: "8px 20px", cursor: "pointer", fontSize: "13px" }}>
                            Connect Facebook
                        </button>
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <select value={fbLeadPageId}
                            onChange={(e) => {
                                const p = fbPages.find(x => x.id === e.target.value);
                                setFbLeadPageId(e.target.value);
                                setFbLeadPageName(p?.name || "");
                                setFbLeadFormId(""); setFbForms([]);
                            }}
                            style={{ width: "100%", fontSize: "13px", padding: "6px 10px", borderRadius: "4px", border: "1px solid #ddd" }}>
                            <option value="">-- Select Facebook Page --</option>
                            {fbPages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>

                        <select value={fbLeadFormId} disabled={!fbLeadPageId}
                            onChange={(e) => {
                                const f = fbForms.find(x => x.id === e.target.value);
                                setFbLeadFormId(e.target.value);
                                setFbLeadFormName(f?.name || "");
                            }}
                            style={{ width: "100%", fontSize: "13px", padding: "6px 10px", borderRadius: "4px", border: "1px solid #ddd" }}>
                            <option value="">-- Select Lead Form --</option>
                            {fbForms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>

                        <div>
                            <div style={{ fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "6px" }}>
                                Map lead fields → attributes
                            </div>
                            {fbLeadFieldMappings.map((m, i) => (
                                <div key={i} style={{ display: "flex", gap: "8px", marginBottom: "6px" }}>
                                    <input placeholder="Field (e.g. full_name)" value={m.fieldName}
                                        onChange={(e) => { const u = [...fbLeadFieldMappings]; u[i] = { ...u[i], fieldName: e.target.value }; setFbLeadFieldMappings(u); }}
                                        style={{ flex: 1, fontSize: "13px", padding: "6px 8px", borderRadius: "4px", border: "1px solid #ddd" }} />
                                    <select value={m.attributeId}
                                        onChange={(e) => { const u = [...fbLeadFieldMappings]; u[i] = { ...u[i], attributeId: e.target.value }; setFbLeadFieldMappings(u); }}
                                        style={{ flex: 1, fontSize: "13px", padding: "6px 8px", borderRadius: "4px", border: "1px solid #ddd" }}>
                                        <option value="">-- Attribute --</option>
                                        {sheetAttributes.map(a => <option key={a.attributeId} value={a.attributeId}>{a.attributeName}</option>)}
                                    </select>
                                    <button onClick={() => setFbLeadFieldMappings(fbLeadFieldMappings.filter((_, idx) => idx !== i))}
                                        style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: "18px", lineHeight: 1 }}>×</button>
                                </div>
                            ))}
                            <button onClick={() => setFbLeadFieldMappings([...fbLeadFieldMappings, { fieldName: "", attributeId: "" }])}
                                style={{ fontSize: "12px", color: "#1d4ed8", background: "none", border: "1px solid #bfdbfe", borderRadius: "4px", padding: "3px 10px", cursor: "pointer" }}>
                                + Add mapping
                            </button>
                        </div>

                        <button onClick={saveFbLeadTrigger} disabled={!fbLeadPageId || !fbLeadFormId}
                            style={{ width: "100%", background: "#1877F2", color: "white", border: "none", borderRadius: "4px", padding: "8px", fontSize: "13px", fontWeight: 600, cursor: "pointer", opacity: (!fbLeadPageId || !fbLeadFormId) ? 0.5 : 1 }}>
                            Save Trigger
                        </button>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default StartFlow;
